import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { isAIDebriefPagePath } from '../../../../config/routes/routeConfig'
import { useGlobalWhereby } from '../../../../shared/hooks/useGlobalWhereby'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../constants/colors'

const RECONNECT_COUNTDOWN_SECONDS = 10

export const WherebyReconnectOverlay: React.FC = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { reconnectPending, startCall, setReconnectPending } = useGlobalWhereby()
  const [secondsLeft, setSecondsLeft] = useState(RECONNECT_COUNTDOWN_SECONDS)
  // The AI debrief page runs a call of its own; bringing the floating one back there puts the player in two.
  const isOnDebriefPage = isAIDebriefPagePath(pathname)
  const isCountingDown = reconnectPending && !isOnDebriefPage

  useEffect(() => {
    if (reconnectPending && isOnDebriefPage) setReconnectPending(false)
  }, [reconnectPending, isOnDebriefPage, setReconnectPending])

  useEffect(() => {
    if (!isCountingDown) return

    setSecondsLeft(RECONNECT_COUNTDOWN_SECONDS)

    const intervalId = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId)
          startCall()
          setReconnectPending(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(intervalId)
  }, [isCountingDown, startCall, setReconnectPending])

  if (!isCountingDown) return null

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)'
      }}
      role="alert"
      aria-live="polite"
      aria-label={t('whereby.reconnectOverlay.message')}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 4, sm: 5, md: 6 },
          borderRadius: '16px',
          backgroundColor: INTERAX_COLORS.offBlack,
          border: `1px solid ${INTERAX_COLORS.white15}`,
          boxShadow: '0 20px 48px rgba(0,0,0,0.65)',
          maxWidth: { xs: '90vw', sm: '400px', md: '420px' }
        }}
      >
        <CircularProgress size={48} sx={{ color: INTERAX_COLORS.sun }} aria-hidden />
        <Typography
          sx={{
            color: INTERAX_COLORS.white,
            fontWeight: 600,
            fontSize: { xs: '16px', sm: '18px', md: '20px' },
            textAlign: 'center'
          }}
        >
          {t('whereby.reconnectOverlay.message')}
        </Typography>
        <Typography
          sx={{
            color: INTERAX_COLORS.typographyFont,
            fontSize: { xs: '14px', sm: '15px', md: '16px' },
            textAlign: 'center'
          }}
        >
          {t('whereby.reconnectOverlay.countdown', { seconds: secondsLeft })}
        </Typography>
      </Box>
    </Box>,
    document.body
  )
}
